import { supabase } from "./supabase";
import type { Settlement } from "@/mocks/settlements";
import type { SupportTicket } from "@/mocks/support";
import type { TeamUser, NotificationSetting } from "@/mocks/settings";
import type { Withdrawal } from "@/mocks/withdrawals";

const AR_MONTHS = [
  "يناير",
  "فبراير",
  "مارس",
  "أبريل",
  "مايو",
  "يونيو",
  "يوليو",
  "أغسطس",
  "سبتمبر",
  "أكتوبر",
  "نوفمبر",
  "ديسمبر",
];

export function formatArabicDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${AR_MONTHS[d.getMonth()]}`;
}

interface SettlementRow {
  id: number;
  period: string;
  date_range: string;
  orders_count: number;
  sales: string;
  commission_rate: number;
  commission: string;
  fines: string;
  net: string;
  status: string;
  status_label: string;
  fines_list: Settlement["finesList"];
  bank_info: Settlement["bank"];
  disputes: Settlement["disputes"];
}

export async function fetchSettlements(providerId: number): Promise<Settlement[]> {
  const { data, error } = await supabase
    .from("settlements")
    .select("*")
    .eq("provider_id", providerId)
    .order("id", { ascending: true });

  if (error) throw error;

  return ((data ?? []) as SettlementRow[]).map((r) => ({
    id: r.id,
    period: r.period,
    dateRange: r.date_range,
    orders: r.orders_count,
    sales: r.sales,
    commissionRate: r.commission_rate,
    commission: r.commission,
    fines: r.fines,
    net: r.net,
    status: r.status as Settlement["status"],
    statusLabel: r.status_label,
    finesList: r.fines_list ?? [],
    bank: r.bank_info ?? null,
    disputes: r.disputes ?? [],
  }));
}

interface TicketRow {
  id: number;
  ticket_number: string;
  type: string;
  status: string;
  status_label: string;
  created_at: string;
}

export async function fetchTickets(providerId: number): Promise<SupportTicket[]> {
  const { data, error } = await supabase
    .from("support_tickets")
    .select("*")
    .eq("provider_id", providerId)
    .order("id", { ascending: false });

  if (error) throw error;

  return ((data ?? []) as TicketRow[]).map((r) => ({
    id: r.ticket_number,
    type: r.type,
    date: formatArabicDate(r.created_at),
    status: r.status as SupportTicket["status"],
    statusLabel: r.status_label,
  }));
}

interface TeamUserRow {
  id: number;
  name: string;
  email: string;
  role: string;
  status: string;
  is_me: boolean;
}

export async function fetchTeamUsers(providerId: number): Promise<TeamUser[]> {
  const { data, error } = await supabase
    .from("team_users")
    .select("*")
    .eq("provider_id", providerId)
    .order("id", { ascending: true });

  if (error) throw error;

  return ((data ?? []) as TeamUserRow[]).map((r) => ({
    id: r.id,
    name: r.name,
    email: r.email,
    role: r.role,
    status: r.status,
    isMe: r.is_me,
  }));
}

interface NotificationRow {
  id: number;
  label: string;
  push: boolean;
  sms: boolean;
  mandatory: boolean;
}

export async function fetchNotifications(providerId: number): Promise<NotificationSetting[]> {
  const { data, error } = await supabase
    .from("notification_settings")
    .select("*")
    .eq("provider_id", providerId)
    .order("id", { ascending: true });

  if (error) throw error;

  return ((data ?? []) as NotificationRow[]).map((r) => ({
    id: r.id,
    label: r.label,
    push: r.push,
    sms: r.sms,
    mandatory: r.mandatory,
  }));
}

interface WithdrawalRow {
  id: number;
  provider_id: number;
  amount: number;
  status: string;
  bank_name: string;
  iban: string;
  account_name: string;
  created_at: string;
}

const WITHDRAWAL_STATUS_LABEL: Record<string, string> = {
  pending: "قيد المراجعة",
  approved: "تمت الموافقة",
  rejected: "مرفوض",
  transferred: "تم التحويل ✓",
};

export async function fetchWithdrawals(providerId: number): Promise<Withdrawal[]> {
  const { data, error } = await supabase
    .from("withdrawals")
    .select("*")
    .eq("provider_id", providerId)
    .order("id", { ascending: false });

  if (error) throw error;

  return ((data ?? []) as WithdrawalRow[]).map((r) => ({
    id: r.id,
    amount: String(r.amount),
    status: r.status as Withdrawal["status"],
    statusLabel: WITHDRAWAL_STATUS_LABEL[r.status] ?? "قيد المراجعة",
    bankName: r.bank_name,
    iban: r.iban,
    accountName: r.account_name,
    date: formatArabicDate(r.created_at),
  }));
}